/**
 * Per-card display config keyed by backend signal_type.
 *
 * Agent labels (chip + section header), descriptions, and the agent icon
 * all come from the backend AGENT_REGISTRY via the company-details API.
 * This file holds only the per-CARD presentation details that are
 * inherently frontend concerns:
 *
 * - icon: shown inside the card body (still per-signal so the two
 *   Follow-Ups subtypes — schedule_follow_up and overdue_reminder — can
 *   carry their own visual cue inside the card)
 * - linkTarget: where the card title navigates (deal vs company vs auto)
 * - formatLabel: the days-based subtitle ("2d unreplied", "Due today")
 *
 * The chip header label comes from `RecommendationAgent.singular_label`
 * via the company-details API — never hardcoded here.
 */

import type { LucideIcon } from 'lucide-react'
import { Mail, Coffee, ArrowRight, CalendarClock, Bell } from 'lucide-react'
import type {
  SignalType,
  EnrichedAction,
} from '../../services/recommendations_contracts'

export interface SignalTypeConfig {
  /** Lucide icon shown inside the card body */
  icon: LucideIcon
  /**
   * Click target for the card's deal/company link.
   * 'deal' always links to the deal, 'company' always to the company,
   * 'auto' links to deal if deal_id exists, else company.
   *
   * Mirrored on the backend in
   * `server/recommendations/usecases/apply_recommendation.py` as
   * `_NOTE_TARGET_BY_SIGNAL` so `add_note` lands on the same entity the
   * card title navigates to. Update both files when adding a signal type.
   */
  linkTarget: 'deal' | 'company' | 'auto'
  /** Format the days-based label shown in the card subtitle */
  formatLabel: (action: EnrichedAction) => string
}

export const SIGNAL_CONFIG: Record<SignalType, SignalTypeConfig> = {
  unreplied_email: {
    icon: Mail,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_unreplied ?? 0}d unreplied`,
  },
  inactive_deal: {
    icon: Coffee,
    linkTarget: 'deal',
    formatLabel: (a) => {
      if (a.days_in_stage && a.days_in_stage >= 14) {
        return `${a.days_in_stage}d in ${a.current_stage || 'stage'}`
      }
      return `${a.days_stale ?? 0}d inactive`
    },
  },
  email_stage_change: {
    icon: ArrowRight,
    linkTarget: 'deal',
    formatLabel: (a) =>
      `${a.days_in_stage ?? 0}d in ${a.current_stage || 'stage'}`,
  },
  schedule_follow_up: {
    icon: CalendarClock,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_since_email ?? 0}d ago`,
  },
  overdue_reminder: {
    icon: Bell,
    linkTarget: 'auto',
    formatLabel: (a) => {
      const d = a.days_overdue
      if (d == null) return 'Due soon'
      if (d > 0) return `${d}d overdue`
      if (d === 0) return 'Due today'
      return `Due in ${Math.abs(d)}d`
    },
  },
}

// Per-action execution lives on the server — see POST
// /recommendations/<rec_id>/apply/ and ApplyRecommendationUsecase.
// The client just packages user inputs into a single FormData and posts;
// auth, validation, side effects, atomicity are enforced server-side.
